import type { FocusTrapProps } from "focus-trap-react";
import { useMemo } from "react";

type FocusTrapOptions = NonNullable<FocusTrapProps["focusTrapOptions"]>;
type TrapContainer = HTMLElement | SVGElement;

/** The longest time to wait for the content of a trap, in milliseconds. */
const MAX_WAIT_MS = 1000;

const DIALOG_SELECTOR = '[role="dialog"]';

/**
 * Returns focus-trap options for a trap whose content a react-spring
 * transition renders.
 *
 * The transition renders the content after the trap activates. The trap waits
 * until the container holds a `role="dialog"` element, then looks for a
 * tabbable node. If the dialog has no tabbable node, the trap focuses the
 * dialog element. That element must have `tabIndex={-1}`.
 *
 * If the trap deactivates during the wait, the wait stops and the trap does
 * not finish its activation.
 *
 * The options own `onDeactivate`. Do not override it.
 */
export const useTransitionFocusTrapOptions = (): FocusTrapOptions =>
  useMemo(() => {
    let containers: TrapContainer[] = [];
    let activation = 0;

    const waitForDialog = (container: TrapContainer, current: number) =>
      new Promise<void>((resolve) => {
        const deadline = performance.now() + MAX_WAIT_MS;
        const check = () => {
          if (current !== activation) {
            // Deactivated. Leave the promise pending.
            return;
          }
          if (
            container.querySelector(DIALOG_SELECTOR) ||
            performance.now() >= deadline
          ) {
            resolve();
          } else {
            requestAnimationFrame(check);
          }
        };
        check();
      });

    return {
      checkCanFocusTrap: async (trapContainers) => {
        containers = trapContainers;
        const current = activation;
        await Promise.all(
          trapContainers.map((container) => waitForDialog(container, current)),
        );
      },
      onDeactivate: () => {
        activation += 1;
      },
      fallbackFocus: () => {
        // focus-trap calls checkCanFocusTrap first, so `containers` is set.
        const [container] = containers;
        if (!container) {
          throw new Error("The focus trap has no container.");
        }
        // The container is a last resort, when no dialog arrived in time.
        return (
          container.querySelector<HTMLElement>(DIALOG_SELECTOR) ?? container
        );
      },
    };
  }, []);
